import { useEffect, useRef, useState } from 'react'
import Script from 'next/script'
import s from './TabbyPromo.module.scss'
import Tabby from '@components/icons/Tabby'
import { Money } from '@pylot-data/pylotschema'
import { useRouter } from 'next/router'
import { useStoreConfig } from '@config/hooks/useStoreConfig'

type TabbyPromoConstructor = new (config: {
    selector: string
    currency: string
    price: number | string
    lang: string
    source: string
    publicKey: string
    merchantCode?: string
}) => void

declare global {
    interface Window {
        TabbyPromo: TabbyPromoConstructor
    }
}

type Props = {
    readonly price: Money
}

export default function TabbyPromoWidget({ price }: Props) {
    const [scriptLoaded, setScriptLoaded] = useState(false)
    const { locale } = useRouter()
    const containerRef = useRef<HTMLDivElement>(null)
    const tabbyInstanceRef = useRef<InstanceType<TabbyPromoConstructor> | null>(
        null
    )
    const config = useStoreConfig()
    const tabbyPublicKey = config?.base?.paymentWidgets?.tabby?.publicKey

    const initializeTabby = () => {
        if (!containerRef.current || !tabbyPublicKey) return
        if (
            !globalThis.window?.TabbyPromo ||
            typeof globalThis.window.TabbyPromo !== 'function'
        ) {
            return
        }

        tabbyInstanceRef.current = new globalThis.window.TabbyPromo({
            selector: '#TabbyPromo',
            currency: price.currency || 'AED',
            price: price.value || 0,
            lang: locale ?? 'en',
            source: 'product',
            publicKey: tabbyPublicKey
        })
    }

    useEffect(() => {
        if (globalThis?.window?.TabbyPromo && scriptLoaded) {
            initializeTabby()
        }
    }, [scriptLoaded, price.value, price.currency, locale])

    return (
        <>
            <div className={s['tabby-wrapper']}>
                {scriptLoaded && (
                    <div className={s['custom-ui']}>
                        <Tabby />
                    </div>
                )}

                <div
                    ref={containerRef}
                    id="TabbyPromo"
                    className={s['tabby-promo-container']}
                />
            </div>
            <Script
                id="tabby-widget"
                src="https://checkout.tabby.ai/tabby-promo.js"
                strategy="afterInteractive"
                onReady={() => {
                    setScriptLoaded(true)
                    initializeTabby()
                }}
            />
        </>
    )
}
